import { defineComponent as _defineComponent } from 'vue'
import { createVNode as _createVNode, createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, createElementVNode as _createElementVNode, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, toDisplayString as _toDisplayString, createBlock as _createBlock, createCommentVNode as _createCommentVNode } from "vue"

const _hoisted_1 = { class: "survey-form" }
const _hoisted_2 = { class: "survey-form__helper" }
const _hoisted_3 = {
  key: 0,
  class: "questions"
}
const _hoisted_4 = { class: "questions__list" }
const _hoisted_5 = { class: "question__text" }
const _hoisted_6 = {
  key: 3,
  class: "question__checkbox"
}
const _hoisted_7 = {
  key: 5,
  class: "question__unsupported"
}
const _hoisted_8 = {
  key: 1,
  class: "empty-state"
}
const _hoisted_9 = { class: "empty-state__icon" }
const _hoisted_10 = {
  key: 0,
  class: "survey-action-section"
}

import { computed, onMounted, reactive, ref, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import AppSelectField from "@/shared/components/ui/AppSelectField.vue";
import AppTextField from "@/shared/components/ui/AppTextField.vue";
import { fetchSurveyTemplates } from "@/shared/services/surveys.api";
import AppButtonFlat from "@/shared/components/ui/AppButtonFlat.vue";
import { submitAppointmentSurvey } from "@/shared/services/appointments.api";

type SurveyQuestion = {
  id: number;
  question: string;
  question_type: string;
  seq_no: number;
  question_code: string;
  options: Array<string | { label: string; value: string | number }>;
};
type SurveyTemplate = Record<string, unknown>;

type QuestionType = "text" | "textarea" | "radio" | "checkbox" | "date" | string;
type QuestionOption = { label: string; value: string | number };


export default /*@__PURE__*/_defineComponent({
  __name: 'AppointmentSurveyForm',
  setup(__props) {

const surveyTemplates = ref<SurveyTemplate[]>([]);
const selectedTemplateId = ref<string | number | "">("");
const answers = reactive<Record<string, unknown>>({});
const route = useRoute();
const router = useRouter();
const isSubmitting = ref(false);
function getTemplateTitle(template: SurveyTemplate): string {
  const title =
    template.title ?? template.name ?? template.template_name ?? template.label;
  return title ? String(title) : "Untitled template";
}

function getTemplateId(template: SurveyTemplate): string | number {
  const id = template.id ?? template.template_id ?? template.uuid ?? template.slug;
  return typeof id === "number" || typeof id === "string"
    ? id
    : getTemplateTitle(template);
}

function extractQuestions(template: SurveyTemplate): SurveyQuestion[] {
  const list = (template as any)?.questions ?? template;
  return Array.isArray(list) ? (list as SurveyQuestion[]) : [];
}

function normalizeQuestionType(raw: unknown): QuestionType {
  const type = String(raw ?? "text")
    .toLowerCase()
    .trim();
  if (type === "text") return "text";
  if (type === "textarea") return "textarea";
  if (type === "radio") return "radio";
  if (type === "checkbox") return "checkbox";
  if (type === "date") return "date";
  return type;
}

function extractOptions(question: SurveyQuestion): QuestionOption[] {
  const raw = question.options;
  if (!Array.isArray(raw)) return [];
  return raw
    .map((opt, idx) => {
      if (typeof opt === "string") return { label: opt, value: opt };
      if (typeof opt === "number") return { label: String(opt), value: opt };
      if (opt && typeof opt === "object") {
        const label =
          (opt as any).label ?? String((opt as any).value ?? `Option ${idx + 1}`);
        const value = (opt as any).value ?? label;
        if (typeof value !== "string" && typeof value !== "number") return null;
        return { label: String(label), value };
      }
      return null;
    })
    .filter((v): v is QuestionOption => Boolean(v));
}

const templateOptions = computed(() =>
  surveyTemplates.value.map((t) => ({
    title: getTemplateTitle(t),
    value: getTemplateId(t),
  }))
);

const selectedTemplate = computed(() => {
  if (selectedTemplateId.value === "") return null;
  return (
    surveyTemplates.value.find((t) => getTemplateId(t) === selectedTemplateId.value) ??
    null
  );
});

const selectedQuestions = computed(() => {
  const t = selectedTemplate.value;
  if (!t) return [];
  const questions = extractQuestions(t);
  return questions.map((q, idx) => {
    const text = q.question || "Question";
    const type = normalizeQuestionType(q.question_type);
    const options = extractOptions(q);
    const key = q.question_code || String(q.id ?? idx);
    return { key: String(key), text, type, options };
  });
});

watch(
  () => selectedTemplateId.value,
  () => {
    for (const k of Object.keys(answers)) delete answers[k];
    for (const q of selectedQuestions.value) {
      answers[q.key] = q.type === "checkbox" ? [] : q.type === "date" ? null : "";
    }
  }
);

function isQuestionAnswered(questionKey: string, questionType: QuestionType): boolean {
  const value = answers[questionKey];

  if (questionType === "checkbox") {
    if (Array.isArray(value)) {
      return value.some((item) => String(item ?? "").trim().length > 0);
    }
    if (typeof value === "string") {
      return value
        .split(",")
        .map((item) => item.trim())
        .filter(Boolean).length > 0;
    }
    return false;
  }

  if (questionType === "date") {
    if (value instanceof Date) {
      return !Number.isNaN(value.getTime());
    }
    if (typeof value === "number") {
      return Number.isFinite(value);
    }
    if (typeof value === "string") {
      return value.trim().length > 0;
    }
    return false;
  }

  if (typeof value === "string") {
    return value.trim().length > 0;
  }

  if (typeof value === "number") {
    return Number.isFinite(value);
  }

  if (value instanceof Date) {
    return !Number.isNaN(value.getTime());
  }

  if (Array.isArray(value)) {
    return value.length > 0;
  }

  return value !== null && value !== undefined && String(value).trim() !== "";
}

async function loadSurveyTemplates() {
  try {
    const response = await fetchSurveyTemplates();
    // eslint-disable-next-line no-console
    console.log("fetchSurveyTemplates response:", response);
    const maybeWrapped = (response as any)?.data;
    const list = Array.isArray(maybeWrapped) ? maybeWrapped : response;
    surveyTemplates.value = Array.isArray(list) ? list : [];
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error("fetchSurveyTemplates failed", error);
  }
}

onMounted(async () => {
  await loadSurveyTemplates();
});

async function handleSubmitSurvey() {
  const rawAppointmentId =
    (route.params as any)?.appointmentId ?? (route.params as any)?.id ?? "";
  const appointmentId = Number(rawAppointmentId);
  if (!Number.isInteger(appointmentId)) return;
  const payload = {
    survey_id: selectedTemplateId.value,
    survey_data: selectedQuestions.value.map((q) => ({
      question_code: q.key,
      question: q.text,
      question_type: q.type,
      options: q.options,
      answer: answers[q.key],
    })),
  };
  try {
    isSubmitting.value = true;
    const response = await submitAppointmentSurvey(appointmentId, payload);
    isSubmitting.value = false;
    
    const rawSubmittedId = response?.data?.id;
    const submittedId = Number(rawSubmittedId);

    await router.push({
      name: "provider.survey.success",
      params: { id: Number.isInteger(submittedId) ? submittedId : appointmentId },
    });
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error("submitAppointmentSurvey failed:", error);
  }
}

return (_ctx: any,_cache: any) => {
  const _component_v_btn = _resolveComponent("v-btn")!
  const _component_v_textarea = _resolveComponent("v-textarea")!
  const _component_v_radio = _resolveComponent("v-radio")!
  const _component_v_radio_group = _resolveComponent("v-radio-group")!
  const _component_v_checkbox = _resolveComponent("v-checkbox")!
  const _component_v_date_input = _resolveComponent("v-date-input")!
  const _component_v_icon = _resolveComponent("v-icon")!

  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createElementVNode("div", _hoisted_1, [
      _createVNode(AppSelectField, {
        modelValue: selectedTemplateId.value,
        "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((selectedTemplateId).value = $event)),
        items: templateOptions.value,
        "item-title": "title",
        "item-value": "value",
        label: "Choose Survey template",
        placeholder: "Select a template"
      }, null, 8, ["modelValue", "items"]),
      _createElementVNode("div", _hoisted_2, [
        _createVNode(_component_v_btn, {
          variant: "text",
          density: "compact",
          color: "info",
          "prepend-icon": "mdi-refresh",
          onClick: _cache[1] || (_cache[1] = ($event: any) => (
          _ctx.$router.push({
            name: 'provider.appointment.saved-templates',
            query: { appointmentId: _ctx.$route.params.appointmentId },
          })
        ))
        }, {
          default: _withCtx(() => [...(_cache[3] || (_cache[3] = [
            _createTextVNode(" Load an existing saved survey ", -1)
          ]))]),
          _: 1
        })
      ]),
      (selectedTemplate.value)
        ? (_openBlock(), _createElementBlock("div", _hoisted_3, [
            _cache[4] || (_cache[4] = _createElementVNode("div", { class: "questions__title my-4" }, "Fill up the survey form", -1)),
            _createElementVNode("div", _hoisted_4, [
              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(selectedQuestions.value, (q) => {
                return (_openBlock(), _createElementBlock("div", {
                  key: q.key,
                  class: "questions__item mb-4"
                }, [
                  _createElementVNode("div", _hoisted_5, _toDisplayString(q.text), 1),
                  (q.type === 'text')
                    ? (_openBlock(), _createBlock(AppTextField, {
                        key: 0,
                        modelValue: answers[q.key],
                        "onUpdate:modelValue": ($event: any) => ((answers[q.key]) = $event),
                        placeholder: "Type your answer"
                      }, null, 8, ["modelValue", "onUpdate:modelValue"]))
                    : (q.type === 'textarea')
                      ? (_openBlock(), _createBlock(_component_v_textarea, {
                          key: 1,
                          modelValue: answers[q.key],
                          "onUpdate:modelValue": ($event: any) => ((answers[q.key]) = $event),
                          "auto-grow": "",
                          placeholder: "Type your answer",
                          variant: "solo",
                          flat: "",
                          "hide-details": "auto",
                          class: "question__textarea"
                        }, null, 8, ["modelValue", "onUpdate:modelValue"]))
                      : (q.type === 'radio')
                        ? (_openBlock(), _createBlock(_component_v_radio_group, {
                            key: 2,
                            modelValue: answers[q.key],
                            "onUpdate:modelValue": ($event: any) => ((answers[q.key]) = $event),
                            "hide-details": "auto",
                            class: "question__radio"
                          }, {
                            default: _withCtx(() => [
                              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(q.options, (opt) => {
                                return (_openBlock(), _createBlock(_component_v_radio, {
                                  key: opt.value,
                                  label: opt.label,
                                  value: opt.value
                                }, null, 8, ["label", "value"]))
                              }), 128))
                            ]),
                            _: 2
                          }, 1032, ["modelValue", "onUpdate:modelValue"]))
                        : (q.type === 'checkbox')
                          ? (_openBlock(), _createElementBlock("div", _hoisted_6, [
                              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(q.options, (opt) => {
                                return (_openBlock(), _createBlock(_component_v_checkbox, {
                                  key: opt.value,
                                  modelValue: answers[q.key],
                                  "onUpdate:modelValue": ($event: any) => ((answers[q.key]) = $event),
                                  label: opt.label,
                                  value: opt.value,
                                  "hide-details": "auto"
                                }, null, 8, ["modelValue", "onUpdate:modelValue", "label", "value"]))
                              }), 128))
                            ]))
                          : (q.type === 'date')
                            ? (_openBlock(), _createBlock(_component_v_date_input, {
                                key: 4,
                                modelValue: answers[q.key],
                                "onUpdate:modelValue": ($event: any) => ((answers[q.key]) = $event),
                                variant: "solo",
                                flat: "",
                                "hide-details": "auto",
                                "prepend-icon": "",
                                placeholder: "Select a date",
                                class: "question__date"
                              }, null, 8, ["modelValue", "onUpdate:modelValue"]))
                            : (_openBlock(), _createElementBlock("div", _hoisted_7, " Unsupported question type: " + _toDisplayString(q.type), 1))
                ]))
              }), 128))
            ])
          ]))
        : (_openBlock(), _createElementBlock("div", _hoisted_8, [
            _createElementVNode("div", _hoisted_9, [
              _createVNode(_component_v_icon, {
                size: "36",
                color: "primary"
              }, {
                default: _withCtx(() => [...(_cache[5] || (_cache[5] = [
                  _createTextVNode("mdi-clipboard-text-outline", -1)
                ]))]),
                _: 1
              })
            ]),
            _cache[6] || (_cache[6] = _createElementVNode("div", { class: "empty-state__title" }, "Select a survey template", -1)),
            _cache[7] || (_cache[7] = _createElementVNode("div", { class: "empty-state__subtitle" }, " Choose a template from the dropdown above to load questions and start filling out the survey for this appointment. ", -1))
          ]))
    ]),
    (selectedTemplate.value)
      ? (_openBlock(), _createElementBlock("div", _hoisted_10, [
          _createVNode(AppButtonFlat, {
            block: "",
            onClick: _cache[2] || (_cache[2] = ($event: any) => (handleSubmitSurvey())),
            loading: isSubmitting.value,
            "append-icon": "mdi-send"
          }, {
            default: _withCtx(() => [...(_cache[8] || (_cache[8] = [
              _createTextVNode("Submit", -1)
            ]))]),
            _: 1
          }, 8, ["loading"])
        ]))
      : _createCommentVNode("", true)
  ], 64))
}
}

})